import React, { useEffect, useState } from "react";
import { Col, Form, message, Row, Skeleton } from "antd";
import {
  CancelButton,
  InputDate,
  InputText,
  InputTextArea,
  ModalComponent,
  SaveButton,
  SelectWithSearch,
  UploadFile,
} from "@comps/components";
import { createSelectList } from "@comps/commonFunctions";
import { getErrorProps, makeRequest, makeRequestStateless } from "@utils/helpers";
import { getDocumentTypesReq, updateDocumentReq } from "../requests";
import dayjs from "dayjs";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { employeeTooltips } from "../../common/employeeTooltips";

const formName = "updateEmploymentDocument";
const fieldLabel = makeFieldLabel(employeeTooltips.document, { namespace: "document" });
const acceptedFiles = ".pdf,.jpg,.png,.jpeg,.doc,.docx";

const UpdateDocument = (props) => {
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    makeRequestStateless(getDocumentTypesReq, false, onDocumentTypesLoaded, onDocumentTypesError);
    setRecord();
    // eslint-disable-next-line
  }, []);

  const onDocumentTypesLoaded = (response) => {
    const data = Object.values(response.resources?.data || {});
    setDocumentTypes(createSelectList(data));
    setLoading(false);
  };

  const onDocumentTypesError = () => {
    setDocumentTypes([]);
    setLoading(false);
  };

  const setRecord = () => {
    if (!props.record) return;
    setFileList(props.record.attachment ? [props.record.attachment] : []);
    form.setFieldsValue({
      ...props.record,
      expiry_date: props.record.expiry_date ? dayjs(props.record.expiry_date) : null,
      additional_info: props.record.additional_info || null,
    });
  };

  const onSubmit = (data) => {
    if (!fileList.length) {
      message.warning("Attachment is required.");
      return;
    }

    setSaving(true);
    const payload = {
      my_document: JSON.stringify({
        id: props.record.id,
        name: data.name,
        document_type_id: data.document_type_id,
        expiry_date: data.expiry_date ? data.expiry_date.format("YYYY-MM-DD") : null,
        additional_info: data.additional_info || null,
      }),
      employmentId: props.employmentId,
      filters: props.filters,
    };
    if (fileList.length > 0 && fileList[0].id === undefined) {
      payload.file_attachment = fileList[0].originFileObj;
    }
    makeRequest(setLoader, updateDocumentReq, payload, onSuccess, onError);
  };

  const onSuccess = (data) => {
    setSaving(false);
    message.success("Document updated successfully.");
    props.onCompleted(data.my_documents.data);
  };

  const onError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Document could not be updated.");
    setErrors(err || []);
  };

  const footer = props.disabled
    ? [<CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />]
    : [
      <SaveButton loading={ saving || loader } form={ formName } key="create_button" htmlType="submit" />,
      <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />,
    ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 900 }
      maskClosable={ false }
      mainTitle={ props.disabled ? "View Document" : "Update Document" }
      subTitle="Employee Documents"
      visible={ true }
      footer={ footer }
      onCancel={ () => props.onCompleted(false) }
    >
      <Skeleton loading={ loading }>
        <Form { ...formLayout } form={ form } disabled={ props.disabled } name={ formName } onFinish={ onSubmit } scrollToFirstError>
          <Row gutter={ 30 }>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="name" rules={ rules.name } label={ fieldLabel("name", "Document Name") } { ...getErrorProps(errors["name"]) }>
                <InputText placeholder="e.g. Warning Letter - July 2026" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="document_type_id"
                rules={ rules.document_type_id }
                label={ fieldLabel("document_type_id", "Document Type") }
                { ...getErrorProps(errors["document_type_id"]) }
              >
                <SelectWithSearch options={ documentTypes } placeholder="Select document type" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="expiry_date" label={ fieldLabel("expiry_date", "Expiry Date") }>
                <InputDate format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="file_attachment" label={ fieldLabel("file_attachment", "Attachment") }>
                <UploadFile fileList={ fileList } setFileList={ setFileList } accept={ acceptedFiles } disabled={ props.disabled } />
              </Form.Item>
            </Col>
            <Col span={ 24 }>
              <Form.Item name="additional_info" label={ fieldLabel("additional_info", "Additional Info") }>
                <InputTextArea placeholder="Optional notes for HR or the employee" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </ModalComponent>
  );
};

export default UpdateDocument;

const rules = {
  name: [{ required: true, message: "Document name is required." }],
  document_type_id: [{ required: true, message: "Document type is required." }],
};

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left",
};
